import { useState } from "react";
import {
  List,
  ListItem,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useNamesQuery, useAddName } from "../hooks/useNames";
import { useGroupProfilesQuery } from "../hooks/useNameProfiles";
import { RELATIONSHIP_OPTIONS, getRelationshipDisplayLabel } from "../utils/relationshipLabels";
import { PANTS_CATEGORIES } from "../utils/profileOptions";
import { logger } from "../utils/logger";

// "36x30 (Men's)"; the bare value when no category is stored (legacy rows).
function formatPantsSize(size, category) {
  const match = PANTS_CATEGORIES.find((c) => c.value === category);
  return match ? `${size} (${match.shortLabel})` : size;
}

export default function NamesList({ userId, onSelectName, activeGroupId, isPendingApproval, isParent }) {
  const { data: names = [] } = useNamesQuery(activeGroupId);
  const { data: groupProfiles = [] } = useGroupProfilesQuery(activeGroupId);
  const addNameMutation = useAddName(activeGroupId);

  const [newName, setNewName] = useState("");
  const [newListType, setNewListType] = useState("me");
  const [otherLabel, setOtherLabel] = useState("");

  const [yourOpen, setYourOpen] = useState(true);
  const [groupOpen, setGroupOpen] = useState(true);
  const [profileDialogNameId, setProfileDialogNameId] = useState(null);

  const yourLists = [...names]
    .filter((n) => n.user_id === userId)
    .sort((a, b) => a.name.localeCompare(b.name));

  const groupLists = [...names]
    .filter((n) => n.user_id !== userId)
    .sort((a, b) => a.name.localeCompare(b.name));

  const hasPersonalList = yourLists.some((n) => !n.is_child_list);
  const listTypeOptions = [
    ...(hasPersonalList ? [] : [{ value: "me", label: "Me" }]),
    ...RELATIONSHIP_OPTIONS,
  ];
  const selectedListType = listTypeOptions.some((o) => o.value === newListType)
    ? newListType
    : listTypeOptions[0]?.value ?? "child";
  const isOtherSelected = selectedListType === "other";

  const addName = async () => {
    if (!newName.trim()) return;
    if (isOtherSelected && !otherLabel.trim()) return;

    const isChildList = isParent && selectedListType !== "me";
    const relationshipLabel = isChildList ? selectedListType : null;
    const relationshipCustomLabel = isOtherSelected ? otherLabel.trim() : null;

    try {
      await addNameMutation.mutateAsync({
        name: newName.trim(),
        userId,
        groupId: activeGroupId,
        isChildList,
        relationshipLabel,
        relationshipCustomLabel,
      });

      setNewName("");
      setNewListType("me");
      setOtherLabel("");
    } catch {
      // Error already logged in namesService; leave the form as-is so the user can retry.
    }
  };

  const renderCounts = (name) => {
    const total = name.total_items ?? 0;

    if (name.user_id === userId && !name.is_child_list) {
      return (
        <Typography variant="caption" color="text.primary">
          {total} item{total !== 1 ? "s" : ""}
        </Typography>
      );
    }

    const claimed = name.claimed_items ?? 0;
    const unclaimed = total - claimed;

    return (
      <Typography variant="caption">
        <Box component="span" sx={{ color: "green", mr: 1 }}>
          Claimed: {claimed}
        </Box>
        |
        <Box component="span" sx={{ color: "red", ml: 1 }}>
          Unclaimed: {unclaimed}
        </Box>
      </Typography>
    );
  };

  const renderNameCard = (n) => {
    return (
      <ListItem
        key={n.id}
        divider
        onClick={() => onSelectName(n.id)}
        sx={{
          cursor: "pointer",
          borderRadius: 3,
          bgcolor: "background.paper",
          boxShadow: "0 14px 32px rgba(0,0,0,0.06)",
          px: 2,
          py: 1.75,
          transition: "transform 0.2s ease",
          '&:hover': { transform: "translateY(-1px)" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", width: "100%", gap: { xs: 0.5, sm: 2 }, minHeight: 72 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              component="span"
              sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", sm: "1.2rem" }, lineHeight: 1.1, color: "royalblue" }}
            >
              {n.name}
            </Typography>
            {n.is_child_list && (
              <Chip label={getRelationshipDisplayLabel(n)} size="small" sx={{ ml: 1 }} />
            )}
            <IconButton
              size="small"
              aria-label="View preferences"
              sx={{ ml: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                setProfileDialogNameId(n.id);
                logger.info('NamesList', 'profile_quickview_opened', { nameId: n.id });
              }}
            >
              <PersonOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
              Created: {new Date(n.created_at).toLocaleString()}
            </Typography>
            <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: { xs: "flex-start", sm: "flex-end" }, gap: 1, flexWrap: "wrap" }}>
              {renderCounts(n)}
            </Box>
          </Box>
        </Box>
      </ListItem>
    );
  };

  if (!activeGroupId) {
    return (
      <Paper elevation={2} className="page-card" sx={{ p: 3, mb: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
          {isPendingApproval
            ? "Your group request is awaiting admin approval. Everyone's lists will appear here once you're approved."
            : "No group selected. Go to Settings to choose a group."}
        </Typography>
      </Paper>
    );
  }

  const dialogName = names.find((n) => n.id === profileDialogNameId) ?? null;
  const dialogProfile = groupProfiles.find((row) => row.name_id === profileDialogNameId) ?? null;

  return (
    <Paper elevation={2} className="page-card" sx={{ p: 3, mb: 4 }}>
      <Stack spacing={3}>
        <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "right" }}>
          {names.length} list{names.length !== 1 ? "s" : ""}
        </Typography>

        {/* Your Lists */}
        <Accordion
          elevation={0}
          expanded={yourOpen}
          onChange={() => setYourOpen((o) => !o)}
          sx={{
            border: "1px solid rgba(232, 200, 74, 0.45)",
            borderRadius: "10px !important",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#e8c84a" }} />}
            sx={{
              bgcolor: "rgba(232, 200, 74, 0.10)",
              borderRadius: "10px",
              "& .MuiAccordionSummary-content": { my: 1 },
            }}
          >
            <Typography sx={{ color: "#e8c84a", fontWeight: 700, fontSize: "0.95rem", letterSpacing: "0.5px" }}>
              ⭐ Your Lists
            </Typography>
          </AccordionSummary>
          <AccordionDetails data-testid="your-lists-panel" sx={{ pt: 2 }}>
            {yourLists.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
                You haven't created any lists yet. Add one below!
              </Typography>
            ) : (
              <List sx={{ display: "grid", gap: 2 }}>
                {yourLists.map(renderNameCard)}
              </List>
            )}
            {(isParent || yourLists.length === 0) && (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {isParent && (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <FormLabel sx={{ fontSize: "0.85rem" }}>This list is for</FormLabel>
                    <Select
                      value={selectedListType}
                      onChange={(e) => setNewListType(e.target.value)}
                      size="small"
                    >
                      {listTypeOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {isParent && isOtherSelected && (
                  <TextField
                    label="Relationship"
                    value={otherLabel}
                    onChange={(e) => setOtherLabel(e.target.value)}
                    size="small"
                    required
                    fullWidth
                  />
                )}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems="stretch"
                >
                  <TextField
                    label="Add Name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={addName}
                    sx={{ minWidth: { xs: "100%", sm: 150 } }}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Group Lists */}
        <Accordion
          elevation={0}
          expanded={groupOpen}
          onChange={() => setGroupOpen((o) => !o)}
          sx={{
            border: "1px solid rgba(76, 175, 130, 0.45)",
            borderRadius: "10px !important",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#4caf82" }} />}
            sx={{
              bgcolor: "rgba(76, 175, 130, 0.10)",
              borderRadius: "10px",
              "& .MuiAccordionSummary-content": { my: 1 },
            }}
          >
            <Typography sx={{ color: "#4caf82", fontWeight: 700, fontSize: "0.95rem", letterSpacing: "0.5px" }}>
              🎄 Group Lists
            </Typography>
          </AccordionSummary>
          <AccordionDetails data-testid="group-lists-panel" sx={{ pt: 2 }}>
            {groupLists.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No other members have created lists yet.
              </Typography>
            ) : (
              <List sx={{ display: "grid", gap: 2 }}>
                {groupLists.map(renderNameCard)}
              </List>
            )}
          </AccordionDetails>
        </Accordion>
      </Stack>

      <Dialog
        open={profileDialogNameId != null}
        onClose={() => setProfileDialogNameId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dialogName ? `${dialogName.name}'s Preferences` : "Preferences"}
        </DialogTitle>
        <DialogContent dividers>
          {!dialogProfile ? (
            <Typography variant="body2" color="text.secondary">
              No preferences shared yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {dialogProfile.age_range && (
                <Typography variant="body2">
                  <strong>Age range:</strong> {dialogProfile.age_range}
                </Typography>
              )}
              {dialogProfile.shirt_size && (
                <Typography variant="body2">
                  <strong>Shirt size:</strong> {dialogProfile.shirt_size}
                </Typography>
              )}
              {dialogProfile.pants_size && (
                <Typography variant="body2">
                  <strong>Pants size:</strong>{" "}
                  {formatPantsSize(dialogProfile.pants_size, dialogProfile.pants_size_category)}
                </Typography>
              )}
              {dialogProfile.shoe_size && (
                <Typography variant="body2">
                  <strong>Shoe size:</strong> {dialogProfile.shoe_size}
                </Typography>
              )}
              {dialogProfile.favorite_brands?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Favorite brands
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {dialogProfile.favorite_brands.map((b) => (
                      <Chip key={b} label={b} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              {dialogProfile.interests?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Interests
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {dialogProfile.interests.map((it) => (
                      <Chip key={it} label={it} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              {dialogProfile.notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {dialogProfile.notes}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogNameId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
